import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownToLine } from "lucide-react";

import {
  paymentFeedApi,
  paymentFeedQueryKeys,
  type PaymentFeedRow,
} from "../../shared/api/platformBilling";
import { apiError } from "../../shared/api/client";
import { Button } from "@/components/ui/button";

const VND = new Intl.NumberFormat("vi-VN");
const dt = (s: string) => new Date(s).toLocaleString("vi-VN");
const d = (s: string) => new Date(s).toLocaleDateString("vi-VN");

const STATUS: Record<string, { label: string; cls: string }> = {
  PAID: { label: "Đã nhận", cls: "text-emerald-700" },
  PENDING: { label: "Chờ trả", cls: "text-amber-700" },
  CANCELLED: { label: "Đã huỷ", cls: "text-muted-foreground" },
  EXPIRED: { label: "Hết hạn", cls: "text-muted-foreground" },
  FAILED: { label: "Lỗi cổng", cls: "text-destructive" },
  RECEIVED_UNAPPLIED: { label: "Nhận nhưng CHƯA vào ví", cls: "text-destructive font-medium" },
};

/**
 * Tiền phòng khám trả Addy — dòng tiền bình thường (#18990).
 *
 * <p>Tách khỏi màn hình <b>Đối soát</b> có chủ ý. Đối soát theo định nghĩa chỉ hiện thứ SAI, nên
 * khi mọi thứ đúng nó rỗng — và người vận hành không biết hôm nay có thu được đồng nào. Một hệ
 * thống thu tiền mà người vận hành chỉ nhìn thấy nó lúc hỏng là một hệ thống chỉ biết kêu.
 */
export function PlatformPaymentFeedPage() {
  // Tự làm mới 15 giây một lần. Đây là màn hình người ta MỞ RA RỒI NHÌN trong lúc chờ tiền về —
  // bắt bấm F5 để biết tiền đã tới chưa thì nó chỉ còn là một bản báo cáo, không phải chỗ theo dõi.
  const feed = useQuery({
    queryKey: paymentFeedQueryKeys.payments(),
    queryFn: () => paymentFeedApi.payments(100),
    refetchInterval: 15_000,
  });
  const daily = useQuery({
    queryKey: paymentFeedQueryKeys.daily(),
    queryFn: () => paymentFeedApi.daily(14),
    refetchInterval: 15_000,
  });

  const settings = useQuery({
    queryKey: paymentFeedQueryKeys.settings(),
    queryFn: () => paymentFeedApi.settings(),
  });
  const vatOn = settings.data?.vatInvoiceEnabled ?? false;

  const rows = feed.data ?? [];
  const days = daily.data ?? [];
  const today = days[0];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="flex items-center gap-2 text-lg font-bold text-foreground">
          <ArrowDownToLine className="h-5 w-5" />
          Tiền vào
        </h1>
        <p className="mt-1 max-w-prose text-sm text-muted-foreground">
          Phòng khám nạp ví và mua gói. Đây là dòng tiền <b>bình thường</b> — màn hình Đối soát chỉ
          hiện thứ sai, nên nó rỗng khi mọi thứ đúng.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Trang tự làm mới mỗi 15 giây{feed.isFetching ? " · đang cập nhật…" : ""}.
        </p>
      </div>

      {(feed.isError || daily.isError) && (
        <p className="text-sm text-destructive">{apiError(feed.error ?? daily.error).detail}</p>
      )}

      <div className="flex flex-wrap gap-3">
        <Stat label="Hôm nay" value={today ? `${VND.format(today.tongVnd)}đ` : "0đ"} sub={today ? `${today.soDon} lệnh` : "chưa có lệnh nào"} />
        <Stat
          label="14 ngày qua"
          value={`${VND.format(days.reduce((a, x) => a + x.tongVnd, 0))}đ`}
          sub={`${days.reduce((a, x) => a + x.soDon, 0)} lệnh đã nhận`}
        />
        <Stat
          label="Đang chờ trả"
          value={`${VND.format(rows.filter((r) => r.status === "PENDING").reduce((a, r) => a + r.amountVnd, 0))}đ`}
          sub={`${rows.filter((r) => r.status === "PENDING").length} lệnh`}
        />
      </div>

      {days.length > 0 && (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[28rem] text-sm">
            <caption className="border-b px-3 py-2 text-left text-sm font-semibold">
              Đã nhận theo ngày
            </caption>
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Ngày</th>
                <th className="px-3 py-2 text-right font-medium">Số lệnh</th>
                <th className="px-3 py-2 text-right font-medium">Tổng</th>
              </tr>
            </thead>
            <tbody>
              {days.map((x) => (
                <tr key={x.ngay} className="border-t">
                  <td className="px-3 py-2">{d(x.ngay)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{x.soDon}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">
                    {VND.format(x.tongVnd)}đ
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[56rem] text-sm">
          <caption className="border-b px-3 py-2 text-left text-sm font-semibold">
            Lệnh gần đây
          </caption>
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Thời gian</th>
              <th className="px-3 py-2 text-left font-medium">Phòng khám</th>
              <th className="px-3 py-2 text-left font-medium">Loại</th>
              <th className="px-3 py-2 text-right font-medium">Số tiền</th>
              <th className="px-3 py-2 text-left font-medium">Cổng / Đơn</th>
              <th className="px-3 py-2 text-left font-medium">Trạng thái</th>
              <th className="px-3 py-2 text-left font-medium">Hoá đơn</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                  Chưa có lệnh thanh toán nào.
                </td>
              </tr>
            ) : (
              rows.map((r) => <FeedRowView key={r.id} row={r} vatOn={vatOn} />)
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FeedRowView({ row, vatOn }: { row: PaymentFeedRow; vatOn: boolean }) {
  const st = STATUS[row.status] ?? { label: row.status, cls: "" };
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const open = useMutation({
    mutationFn: () => paymentFeedApi.openInvoice(row.id),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: paymentFeedQueryKeys.payments() });
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  return (
    <tr className="border-t">
      <td className="px-3 py-2 whitespace-nowrap text-xs">{dt(row.createdAt)}</td>
      <td className="px-3 py-2">
        <div className="font-medium text-foreground">{row.tenantName ?? "—"}</div>
        <div className="font-mono text-xs text-muted-foreground">{row.tenantCode ?? "—"}</div>
      </td>
      <td className="px-3 py-2 text-xs">
        {row.purpose === "TOP_UP" ? "Nạp ví" : "Mua gói"}
      </td>
      <td className="px-3 py-2 text-right tabular-nums font-medium">
        {VND.format(row.amountVnd)}đ
      </td>
      <td className="px-3 py-2 text-xs">
        {row.provider} · <span className="font-mono">{row.orderCode}</span>
      </td>
      <td className={`px-3 py-2 text-xs ${st.cls}`}>{st.label}</td>
      <td className="px-3 py-2 text-xs">
        <InvoiceCell row={row} vatOn={vatOn} busy={open.isPending} onOpen={() => open.mutate()} />
        {error && <div className="mt-1 text-xs text-destructive">{error}</div>}
      </td>
    </tr>
  );
}

/**
 * Ô hoá đơn của một khoản thu.
 *
 * <p>Nút chỉ hiện khi khoản đó ĐÃ NHẬN TIỀN và CHƯA có hoá đơn. Đã xuất rồi thì hiện số hoá đơn
 * chứ không phải một cái nút bị tắt — một nút xám vẫn mời người ta bấm thử, còn số hoá đơn thì trả
 * lời luôn câu họ định hỏi.
 */
function InvoiceCell({
  row,
  vatOn,
  busy,
  onOpen,
}: {
  row: PaymentFeedRow;
  vatOn: boolean;
  busy: boolean;
  onOpen: () => void;
}) {
  if (row.status !== "PAID") {
    return <span className="text-muted-foreground">—</span>;
  }
  if (row.invoiceNo) {
    return (
      <span className="text-emerald-700">
        Đã xuất · <span className="font-mono">{row.invoiceNo}</span>
      </span>
    );
  }
  if (row.invoiceStatus === "BLOCKED") {
    return <span className="text-amber-700">Thiếu MST khách</span>;
  }
  if (row.invoiceStatus === "NOT_REQUIRED") {
    return <span className="text-muted-foreground">Khách không lấy</span>;
  }
  if (row.invoiceStatus === "PENDING") {
    return <span className="text-muted-foreground">Đã ghi nhận, chờ xuất</span>;
  }
  if (!vatOn) {
    return <span className="text-muted-foreground">Chưa bật xuất hoá đơn</span>;
  }
  return (
    <Button size="sm" variant="outline" className="h-7" disabled={busy} onClick={onOpen}>
      {busy ? "Đang mở…" : "Xuất hoá đơn"}
    </Button>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="min-w-[10rem] flex-1 rounded-lg border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-xl font-bold tabular-nums text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </div>
  );
}
